import './App.scss';
import Navigator from "./navigation/navigator";
import AlertComponent from "./shared/components/alert/alertComponent";
import {ColorConfig} from "./constants";
import {
    logout,
    updateLastActivityTime
} from "./store/actions/account.action";
import CheckLoginComponent from "./shared/components/check-login/checkLoginComponent";
import ConfirmationComponent from "./shared/components/confirmation/confirmationComponent";
import {createTheme, ThemeOptions, ThemeProvider} from '@mui/material/styles';
import {useDispatch, useSelector} from "react-redux";
import {CommonService} from "./shared/services";
import SystemLockComponent from "./shared/components/system-lock/SystemLockComponent";
import moment from "moment";
import {
    getAccountExecutiveList,
    getAccountPreferenceStaticData,
    getAllLocationListLite,
    getBillingModeList,
    getCityListLite,
    getEquipmentTypeList,
    getEthnicityData, getExpressionsListLite,
    getFacilityListLite,
    getFacilityStatus,
    getFacilityTypes,
    getInsuranceCompanyData,
    getInsurancePlanData,
    getLabListLite,
    getLabStatus,
    getLocationListLite,
    getLocationStatus,
    getPanelCodeList,
    getPayorMixList,
    getPhysicianListLite,
    getRaceData,
    getRelationshipData, getReportTypeData, getSampleCollectionDeviceData,
    getSampleStatusList,
    getSampleTypesList, getTestListLite,
    getTestOrderStatus,
    getTestPanelList,
    getTestPanelStatus,
    getUserList,
    getBiomarkerList, getAgeList, getRoleList, getSystemConfig, getSampleTypesWithCollectionDevices,
    getSuffixList,
    getPrefixList,
    getMaritalStatusList, getModulePrimaryAttachment
} from "./store/actions/staticData.action";
import {useEffect, useRef} from "react";
import {IRootReducerState} from "./store/reducers";
import {setQueryParamsData} from "./store/actions/stateManagement.action";
import {useLocation} from "react-router-dom";
import queryString from "query-string";
import {debounceTime, fromEvent, merge} from "rxjs";
import HelpDeskComponent from "./shared/components/help-desk/HelpDeskComponent";
import {setSelectedLab} from "./store/actions/selectedLab.action";
import AttachmentPreviewComponent from "./shared/components/attachment-preview/AttachmentPreviewComponent";
import SignaturePreviewModalComponent from './shared/components/signature-preview-modal/SignaturePreviewModalComponent';

interface AppProps {
    setCurrentUser?: any;
    makeCheckLoginHTTPRequest?: any;
}

export const themeOptions: ThemeOptions = {
    palette: {
        mode: 'light',
        primary: {
            main: ColorConfig.primary,
        },
        text: {
            primary: ColorConfig.textLight,
        },
        background: {
            default: ColorConfig.backgroundColor,
        },
        info: {
            main: ColorConfig.info,
            contrastText: '#FFF'
        },
        secondary: {
            main: ColorConfig.secondary,
            contrastText: '#FFF'
        },
        error: {
            main: ColorConfig.error,
            contrastText: '#FFF'
        },
        success: {
            main: ColorConfig.success,
            contrastText: '#FFF'
        },
        warning: {
            main: ColorConfig.warning,
            contrastText: '#FFF'
        },
    },
    typography: {fontFamily: 'Lato'},
    components: {
        MuiGrid: {
            styleOverrides: {
                container: {
                    marginTop: -10, // Add the top margin globally
                },
            },
        },
    },
};
const theme = createTheme(themeOptions);
const showHelpDeskComponent = true;

const App = (props: AppProps) => {

    const location = useLocation();
    const {token} = useSelector((state: IRootReducerState) => state.account);
    const dispatch = useDispatch();
    const logoutSubscriptionRef = useRef(true);

    useEffect(() => {
        CommonService._communications.logoutSubject.subscribe(() => {
            if (!logoutSubscriptionRef.current) return null;
            dispatch(logout());
        });
        return () => {
            logoutSubscriptionRef.current = false;
        }
    }, [dispatch]);

    useEffect(() => {
        if (token) {
            const mouseMove$ = fromEvent(document, 'mousemove').pipe(debounceTime(1000));
            const keyPress$ = fromEvent(document, 'keypress').pipe(debounceTime(1000));

            const subscription = merge(mouseMove$, keyPress$).subscribe(() => {
                CommonService._communications.UpdateLastActivityTimeSubject.next(moment().unix());
            });

            return () => {
                subscription.unsubscribe();
            };
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            dispatch(updateLastActivityTime());
            dispatch(getInsuranceCompanyData());
            dispatch(getInsurancePlanData());
            dispatch(getRelationshipData());
            dispatch(getEthnicityData());
            dispatch(getRaceData());
            dispatch(getFacilityTypes());
            dispatch(getUserList());
            dispatch(getPayorMixList());
            dispatch(getAccountPreferenceStaticData());
            dispatch(getFacilityListLite());
            dispatch(getLocationListLite());
            dispatch(getAllLocationListLite());
            dispatch(getLabListLite());
            dispatch(getLabStatus());
            dispatch(getFacilityStatus());
            dispatch(getLocationStatus());
            dispatch(getTestOrderStatus());
            dispatch(getTestPanelStatus());
            dispatch(getPhysicianListLite());
            dispatch(getCityListLite());
            dispatch(getSampleTypesList());
            dispatch(getSampleStatusList());
            dispatch(getPanelCodeList());
            dispatch(getBillingModeList());
            dispatch(getAccountExecutiveList());
            dispatch(getEquipmentTypeList());
            dispatch(getTestPanelList());
            dispatch(getReportTypeData());
            dispatch(getSampleCollectionDeviceData());
            dispatch(getTestListLite());
            dispatch(getExpressionsListLite());
            dispatch(getBiomarkerList())
            dispatch(getAgeList())
            dispatch(getRoleList())
            dispatch(getSampleTypesWithCollectionDevices())
            dispatch(getSuffixList())
            dispatch(getPrefixList())
            dispatch(getMaritalStatusList())
            dispatch(getModulePrimaryAttachment())
        } else {
            dispatch(setSelectedLab(undefined))
        }
    }, [dispatch, token]);
    useEffect(() => {
        dispatch(getSystemConfig())
    }, [dispatch]);

    useEffect(() => {
        const queryParams = queryString.parseUrl(location.search).query;
        if (queryParams) {
            dispatch(setQueryParamsData(queryParams));
        }
    }, [dispatch, location]);

    return (
        // <LocalizationProvider dateAdapter={AdapterMoment}>
        <CheckLoginComponent>
            <div className="app">
                <ThemeProvider theme={theme}>
                    <Navigator/>
                    <SignaturePreviewModalComponent/>
                    <AlertComponent/>
                    <ConfirmationComponent/>
                    <SystemLockComponent/>
                    <AttachmentPreviewComponent/>
                    {token && showHelpDeskComponent && <HelpDeskComponent/>}
                </ThemeProvider>
            </div>
        </CheckLoginComponent>
        // </LocalizationProvider>
    );
}

export default App;
